<script lang="ts">
  import { onMount } from 'svelte';

  const mean = 0.575;
  const std = 0.286;

  type Rollout = {
    label: string;
    name: string;
    reward: number;
    tokens: string[];
    advantage: number;
  };

  const rollouts: Rollout[] = [
    {
      label: 'B',
      name: 'Royal Red',
      reward: 0.9,
      tokens: ['Royal', ' Red'],
      advantage: 1.14,
    },
    {
      label: 'C',
      name: 'Rose Gold',
      reward: 0.2,
      tokens: ['Rose', ' Gold'],
      advantage: -1.31,
    },
  ];

  let idx = $state(0);
  let applied = $state(false);

  let current = $derived(rollouts[idx]);

  const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

  onMount(() => {
    let alive = true;
    const loop = async () => {
      await wait(1100);
      while (alive) {
        applied = true;
        await wait(3400);
        if (!alive) break;
        applied = false;
        await wait(900);
        if (!alive) break;
        idx = (idx + 1) % rollouts.length;
        await wait(1100);
      }
    };
    loop();
    return () => {
      alive = false;
    };
  });

  const format = (a: number) => (a > 0 ? '+' : '') + a.toFixed(2);
</script>

<div class="apply">
  <div
    class="row"
    class:above={current.advantage > 0}
    class:below={current.advantage < 0}
  >
    <span class="chip">
      <span class="chip-label">{current.label}</span>
      <span class="chip-sep">·</span>
      <span class="chip-name">{current.name}</span>
    </span>

    <span class="arrow-right" aria-hidden="true"></span>

    <span class="tokens">
      {#each current.tokens as tok, i (i)}
        <span class="token" class:pushed={applied} style:transition-delay="{i * 90}ms">
          {tok}
        </span>
      {/each}
    </span>

    <span class="advantage">
      <span class="adv-label">advantage</span>
      <span class="adv-value">{format(current.advantage)}</span>
    </span>
  </div>

  <div class="formula">
    <div class="formula-line formula-generic">
      advantage = (reward − mean) ÷ std
    </div>
    <div class="formula-line formula-specific">
      = ({current.reward.toFixed(2)} − {mean.toFixed(2)}) ÷ {std.toFixed(2)} = <span
        class="result">{format(current.advantage)}</span
      >
    </div>
  </div>
</div>

<style>
  .apply {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 32px 0;
    font-family: 'Diatype', 'ABC Diatype', Arial, sans-serif;
    color: #333;
    align-items: center;
  }

  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 40px;
  }

  .chip {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    padding: 7px 12px;
    border: 1px solid #0891b2;
    border-radius: 7px;
    font-family: 'Geist Mono', 'SF Mono', ui-monospace, monospace;
    font-size: 15px;
    color: #0891b2;
    line-height: 1;
    background: transparent;
    transition:
      border-color 450ms ease,
      color 450ms ease;
  }

  .above .chip {
    color: #28a948;
    border-color: #28a948;
  }
  .below .chip {
    color: #e7323f;
    border-color: #e7323f;
  }

  .chip-label {
    font-weight: 600;
  }

  .chip-sep {
    opacity: 0.55;
  }

  .arrow-right {
    position: relative;
    display: inline-block;
    width: 18px;
    height: 1px;
    background: #bbb;
  }
  .arrow-right::after {
    content: '';
    position: absolute;
    right: -1px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
    border-left: 6px solid #bbb;
  }

  .tokens {
    display: inline-flex;
    gap: 4px;
  }

  .token {
    padding: 6px 10px;
    border: 1px solid #d0d0d0;
    border-radius: 5px;
    background: #fafafa;
    font-family: 'Geist Mono', 'SF Mono', ui-monospace, monospace;
    font-size: 13px;
    color: #555;
    line-height: 1;
    transition:
      transform 650ms cubic-bezier(0.22, 1, 0.36, 1),
      background-color 500ms ease,
      border-color 500ms ease,
      color 500ms ease;
    will-change: transform;
  }

  .above .token.pushed {
    transform: translateY(-6px);
    background: #e9f8ec;
    border-color: #28a948;
    color: #1d7a34;
  }

  .below .token.pushed {
    transform: translateY(6px);
    background: #fde9ea;
    border-color: #e7323f;
    color: #b91a25;
  }

  .advantage {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border: 1px solid #d5d5d5;
    border-radius: 999px;
    background: white;
    font-family: 'Diatype', 'ABC Diatype', Arial, sans-serif;
    font-size: 13px;
    color: #777;
    transition:
      border-color 450ms ease,
      color 450ms ease,
      background-color 300ms ease;
  }

  .above .advantage {
    color: #28a948;
    border-color: #bfe5c8;
    background: #f4fbf6;
  }

  .below .advantage {
    color: #e7323f;
    border-color: #f4c5c9;
    background: #fdf5f5;
  }

  .adv-label {
    letter-spacing: 0.02em;
  }
  .adv-value {
    font-family: 'Geist Mono', 'SF Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
  }

  .formula {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    font-family: 'Geist Mono', 'SF Mono', ui-monospace, monospace;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    color: #7a7a7a;
    line-height: 1.5;
  }

  .formula-generic {
    color: #9a9a9a;
  }

  .formula-specific {
    color: #555;
  }

  .result {
    font-weight: 500;
    color: #333;
  }

  .above .result {
    color: #1d7a34;
  }
  .below .result {
    color: #b91a25;
  }
</style>
